import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { createLazyIcon } from "../../lib/lazy-icons";
import type { RecurringSeriesInfo } from "../../hooks/useRecurringSeries";

const Repeat = createLazyIcon("Repeat");
const Lock = createLazyIcon("Lock");

interface RecurrenceReadOnlyCardProps {
  series: RecurringSeriesInfo;
  /** Dates actuellement choisies dans le calendrier (pour l'avertissement). */
  selectedDates: string[];
  /** Page Résultats (gestion de série) ; lien masqué si inconnu. */
  manageHref?: string;
}

const FREQ_KEYS: Record<string, string> = {
  weekly: "freq_weekly",
  biweekly: "freq_biweekly",
  monthly: "freq_monthly",
  daily: "freq_daily",
  custom: "freq_custom",
};

/**
 * Récurrence d'un sondage publié, en lecture seule (NAJ-145). Les votes sont
 * indexés par position d'option : la règle ne peut plus changer après
 * publication. Aucun contrôle de règle ici — seulement un lien vers la
 * gestion de série (RecurringSeriesManager).
 */
export default function RecurrenceReadOnlyCard({
  series,
  selectedDates,
  manageHref,
}: RecurrenceReadOnlyCardProps) {
  const { t, i18n } = useTranslation("creator");
  const fmt = (d?: string) =>
    d ? new Date(d).toLocaleDateString(i18n.language) : "";

  const { recurrence_rule: rule, instances } = series;
  const cancelled = instances.filter((i) => i.status === "cancelled").length;
  const active = instances.filter((i) => i.status === "active").length;

  const freqKey = FREQ_KEYS[rule.freq];
  const freqLabel = freqKey ? t(`recurrence.${freqKey}`) : rule.freq;
  const summary =
    rule.freq === "custom"
      ? t("recurrence.readonly_custom_dates", {
          count: rule.dates?.length ?? instances.length,
        })
      : rule.until
        ? t("recurrence.readonly_summary_period", {
            freq: freqLabel,
            from: fmt(rule.dtstart),
            until: fmt(rule.until),
          })
        : freqLabel;

  const reminder =
    series.reminder_days_before == null
      ? t("recurrence.reminder_never")
      : t("recurrence.reminder_days", { count: series.reminder_days_before });

  const known = new Set(instances.map((i) => i.occurrence_date.slice(0, 10)));
  const datesChanged =
    selectedDates.length !== known.size ||
    selectedDates.some((d) => !known.has(d.slice(0, 10)));

  return (
    <section
      data-testid="recurrence-readonly-card"
      className="rounded-xl border border-gray-700 bg-[#1e1e1e] p-4 text-sm text-gray-200 min-w-0"
    >
      <h3 className="flex items-center gap-2 font-semibold text-white">
        <Repeat className="w-4 h-4 shrink-0" />
        <span className="truncate">{t("recurrence.readonly_title")}</span>
      </h3>
      <dl className="mt-3 space-y-1 break-words">
        <div>{summary}</div>
        <div>
          {t("recurrence.reminder_label")} : {reminder}
        </div>
        <div>
          {series.status === "paused"
            ? t("recurrence.readonly_status_paused")
            : t("recurrence.readonly_status_active")}
          {" · "}
          {t("recurrence.readonly_occurrences", { active, cancelled })}
        </div>
      </dl>
      <p className="mt-3 flex items-start gap-2 text-gray-400">
        <Lock className="w-4 h-4 mt-0.5 shrink-0" />
        <span>{t("recurrence.readonly_locked_reason")}</span>
      </p>
      {datesChanged && (
        <p role="alert" className="mt-2 text-amber-400">
          {t("recurrence.readonly_dates_warning")}
        </p>
      )}
      {manageHref && (
        <Link
          to={manageHref}
          className="mt-3 inline-block text-blue-400 underline"
        >
          {t("recurrence.readonly_manage_link")}
        </Link>
      )}
    </section>
  );
}
